<template>
  <div>
    <BubbleHeader :bubble="bubble" />
    <div class="p-6">
      <div class="mb-6">
        <div class="relative w-full max-w-md mx-auto">
          <NuxtImg
            v-if="props.bubble.filePath"
            :src="props.bubble.filePath"
            :alt="bubble.filename"
            class="w-full h-auto object-cover rounded-lg shadow-lg mb-4 cursor-pointer"
            :modifiers="{ fit: 'contain', width: 400, height: 300 }"
            @click="openFullImage"
          />
          <div
            v-if="isFullImageOpen"
            class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            @click="closeFullImage"
          >
            <NuxtImg
              :src="props.bubble.filePath"
              :alt="bubble.filename"
              class="max-w-full max-h-full object-contain"
              :modifiers="{ fit: 'contain', width: 1200, height: 900 }"
            />
          </div>
        </div>
      </div>
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold">{{ bubble.filename }}</h2>
        <Button @click="toggleExifData">
          {{ showExifData ? 'Hide EXIF Data' : 'Show EXIF Data' }}
        </Button>
      </div>
      <div class="mb-6">
        <p v-if="bubble.description" class="text-lg mb-2">{{ bubble.description }}</p>
        <p class="text-sm text-gray-600 mb-1">File size: {{ formatFileSize(bubble.fileSize) }}</p>
        <p v-if="bubble.width && bubble.height" class="text-sm text-gray-600 mb-1">
          Dimensions: {{ bubble.width }}x{{ bubble.height }}
        </p>
        <p v-if="bubble.resolution" class="text-sm text-gray-600 mb-1">
          Resolution: {{ bubble.resolution }}
        </p>
        <p v-if="bubble.colorSpace" class="text-sm text-gray-600 mb-1">
          Color Space: {{ bubble.colorSpace }}
        </p>
      </div>
      <div v-if="bubble.location" class="mb-6">
        <h3 class="text-lg font-semibold mb-2">Location:</h3>
        <p class="text-sm text-gray-600">
          Latitude: {{ bubble.location.latitude }}, Longitude: {{ bubble.location.longitude }}
        </p>
      </div>
      <div v-if="showExifData && bubble.exifData" class="mb-6">
        <h3 class="text-lg font-semibold mb-2">EXIF Data:</h3>
        <ul class="list-disc list-inside">
          <li v-for="(value, key) in bubble.exifData" :key="key" class="text-sm text-gray-600">
            {{ key }}: {{ value }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ImageBubble } from '@/stores/interfaces'
import BubbleHeader from '../BubbleHeader.vue'
import { Button } from '@/components/ui/button'
import { ref } from 'vue'
import { useTypeNavigation } from '@/composables/useTypeNavigation'

const props = defineProps<{
  bubble: ImageBubble
}>()

// Use the type navigation composable
useTypeNavigation(props.bubble)

const showExifData = ref(false)
const isFullImageOpen = ref(false)

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const toggleExifData = () => {
  showExifData.value = !showExifData.value
}

const openFullImage = () => {
  isFullImageOpen.value = true
}

const closeFullImage = () => {
  isFullImageOpen.value = false
}
</script>

<style scoped>
/* Scoped styles for ImageDetailedView */
</style>
